import React from 'react'
import { Box, useTheme } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { StyledIconButton, StyledIcon } from '../../styles'
import { faHighlighter, faEraser, faClose, faGripVertical, faGrip } from '@fortawesome/pro-solid-svg-icons'
import DraggableDialog from '@app/components/ui/modals/DraggableDialog'
import { HighlightContainer, DraggableIcon } from './styles'
import { HighlightMode } from '../../types'

const DRAGGABLE_HIGHLIGHT_ID = 'DRAGGABLE_HIGHLIGHT_ID'

// a11y: describes the keyboard flow; referenced by the Highlight/Erase buttons via aria-describedby
const HIGHLIGHT_INSTRUCTIONS_ID = 'highlight-tool-instructions'

export type Props = {
  isReviewMode: boolean
  isMobile?: boolean
  onClose: () => void
  highlight: Omit<HighlightMode, 'reset'>
}

const DraggableHighlight: React.FC<Props> = ({ isReviewMode, isMobile = false, highlight, onClose }) => {
  const {
    eraseMode,
    highlightMode,
    toggleEraseMode,
    toggleHighlightMode,
    highlightCurrentSelection,
    unhighlightCurrentSelection,
    hasValidSelection,
  } = highlight

  const theme = useTheme()

  if (isReviewMode) return null

  return (
    <DraggableDialog
      paperStyles={{
        boxShadow: theme.shadows[6],
      }}
      id={DRAGGABLE_HIGHLIGHT_ID}
      position='left-bottom'
      open
      onClose={onClose}
      disableBackdropClose
      enableHeader={false}
      customDefaultCoordinates={{ y: isMobile ? 110 : 82, x: 12 }}
      requireDragHandle
      nonModal
    >
      <HighlightContainer isMobile={isMobile} aria-label='Highlight toolbar'>
        <DraggableIcon role="application" aria-label='Drag toolbar' aria-roledescription='draggable' tabIndex={0} isMobile={isMobile} data-drag-handle='true'>
          <StyledIcon icon={isMobile ? faGrip : faGripVertical} />
        </DraggableIcon>
        <StyledIconButton
          aria-label='Highlight'
          title='Highlight (Ctrl+Shift+H)'
          aria-keyshortcuts='Control+Shift+H'
          aria-describedby={HIGHLIGHT_INSTRUCTIONS_ID}
          onClick={() => (hasValidSelection() ? highlightCurrentSelection() : toggleHighlightMode())}
          className={highlightMode ? 'active' : ''}
        >
          <StyledIcon icon={faHighlighter} />
        </StyledIconButton>
        <StyledIconButton
          aria-label='Erase'
          aria-describedby={HIGHLIGHT_INSTRUCTIONS_ID}
          onClick={() => (hasValidSelection() ? unhighlightCurrentSelection() : toggleEraseMode())}
          className={eraseMode ? 'active' : ''}
        >
          <StyledIcon icon={faEraser} />
        </StyledIconButton>
        <StyledIconButton aria-label='Close toolbar' onClick={onClose}>
          <StyledIcon icon={faClose} />
        </StyledIconButton>

        {/* a11y: keyboard-flow hint for the Highlight/Erase buttons (Ready#7707) */}
        <Box component='span' id={HIGHLIGHT_INSTRUCTIONS_ID} sx={visuallyHidden}>
          Select text in the passage or question, then activate to highlight or erase it. Keyboard shortcut:
          Control plus Shift plus H.
        </Box>
      </HighlightContainer>
    </DraggableDialog>
  )
}

export default DraggableHighlight
